import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import type { Client } from '../../../api/types';
import { EventStatusBadge } from '../../../components/ui/Badge';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Button } from '../../../components/ui/Button';
import { formatDate, formatCurrency, formatEventType } from '../../../utils/format';

export default function ClientEventsTab({ client }: { client: Client }) {
  const navigate = useNavigate();
  const events = client.events ?? [];

  if (events.length === 0) {
    return (
      <EmptyState
        icon={<Calendar size={32} />}
        title="Nessun evento"
        description="Questo cliente non ha ancora eventi associati."
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
      {/* Header stats */}
      <div style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1rem 1.25rem',
        display: 'flex',
        alignItems: 'center',
        gap: '2rem',
      }}>
        <div>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.125rem' }}>Totale eventi</p>
          <p style={{ fontSize: 'var(--text-lg)', fontWeight: 700 }}>{events.length}</p>
        </div>
        <div>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.125rem' }}>Valore totale</p>
          <p style={{ fontSize: 'var(--text-lg)', fontWeight: 700 }}>
            {formatCurrency(events.reduce((sum, ev) => sum + (ev.totalAmount ?? 0), 0))}
          </p>
        </div>
      </div>

      {/* Events list */}
      <div style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
      }}>
        {events.map((ev, i) => (
          <button
            key={ev.id}
            onClick={() => navigate(`/events/${ev.id}`)}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
              gap: '1rem',
              padding: '0.875rem 1.25rem',
              borderBottom: i < events.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
              background: 'none',
              border: 'none',
              borderBottomColor: i < events.length - 1 ? 'rgba(255,255,255,0.08)' : 'transparent',
              borderBottomWidth: i < events.length - 1 ? '1px' : '0',
              borderBottomStyle: 'solid',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left',
              transition: 'background var(--transition-interactive)',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--glass-bg-card)')}
            onMouseLeave={e => (e.currentTarget.style.background = '')}
          >
            <div>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text)', marginBottom: '0.2rem' }}>{ev.title}</p>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                {formatEventType(ev.eventType)} · {formatDate(ev.eventDate)} · {formatCurrency(ev.totalAmount)}
              </p>
            </div>
            <EventStatusBadge status={ev.status} />
          </button>
        ))}
      </div>
    </div>
  );
}
